import { useEffect, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { I18nProvider } from "@/i18n/I18nProvider";
import { Nav } from "@/components/festival/Nav";
import { Footer } from "@/components/festival/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import { CheckCircle2, FileText, Upload } from "lucide-react";
import {
  submitSponsorApplication,
  getSponsorInfoPdfUrl,
  SPONSOR_PACKAGES,
  SPONSOR_PACKAGE_LABELS,
  SPONSOR_ACTIVATIONS,
  SPONSOR_ACTIVATION_LABELS,
  type SponsorPackage,
  type SponsorActivation,
} from "@/lib/sponsor-application.functions";

export const Route = createFileRoute("/partner-aanmelden")({
  head: () => ({
    meta: [
      { title: "Partner worden — Openluchtfilmfestival Apeldoorn" },
      { name: "description", content: "Meld je aan als partner van het Openluchtfilmfestival." },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: PartnerSignupPage,
});

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(",")[1] ?? "");
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

function PartnerSignupPage() {
  const [pkg, setPkg] = useState<SponsorPackage | "">("");
  const [activations, setActivations] = useState<SponsorActivation[]>([]);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [terms, setTerms] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [pdf, setPdf] = useState<{ url: string | null; fileName: string | null }>({ url: null, fileName: null });

  useEffect(() => {
    getSponsorInfoPdfUrl().then(setPdf).catch(() => {});
  }, []);

  function toggleActivation(a: SponsorActivation) {
    setActivations((prev) => (prev.includes(a) ? prev.filter((x) => x !== a) : [...prev, a]));
  }

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    if (!pkg) {
      toast.error("Kies een partnerpakket");
      return;
    }
    if (!logoFile) {
      toast.error("Upload een logo (verplicht bij pakketkeuze)");
      return;
    }
    if (!terms) {
      toast.error("Ga akkoord met de partnervoorwaarden");
      return;
    }
    if (logoFile.size > 5 * 1024 * 1024) {
      toast.error("Logo mag maximaal 5MB zijn");
      return;
    }
    setSubmitting(true);
    try {
      const dataBase64 = await fileToBase64(logoFile);
      await submitSponsorApplication({
        data: {
          company: String(fd.get("company") ?? "").trim(),
          contact_person: String(fd.get("contact_person") ?? "").trim(),
          email: String(fd.get("email") ?? "").trim(),
          phone: String(fd.get("phone") ?? "").trim(),
          package: pkg as SponsorPackage,
          activations,
          notes: String(fd.get("notes") ?? "").trim(),
          terms_agreed: true,
          logo: { name: logoFile.name, type: logoFile.type || "image/png", dataBase64 },
        },
      });
      setDone(true);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Er ging iets mis";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <I18nProvider lang="nl">
      <div className="min-h-screen bg-background">
        <Nav />
        <main className="pt-28 pb-24 md:pt-36">
          <section className="mx-auto max-w-3xl px-5 md:px-8">
            <div className="mb-8">
              <p className="font-mono-cinema text-[10px] text-gold">— Word partner</p>
              <h1 className="mt-3 font-display text-4xl font-medium leading-[1.05] md:text-5xl">
                Partner van het <span className="italic text-gold">Openluchtfilmfestival</span>
              </h1>
              <p className="mt-4 text-foreground/75">
                Verbind je merk aan negen zomeravonden openluchtbioscoop in Stadspark Berg &amp; Bos.
                Vul het formulier in — we nemen daarna persoonlijk contact met je op.
              </p>
              {pdf.url && (
                <a
                  href={pdf.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-5 inline-flex items-center gap-2 rounded-full border border-gold/40 bg-night-deep/60 px-4 py-2 text-sm text-gold hover:bg-night-deep"
                >
                  <FileText className="h-4 w-4" />
                  Bekijk partnermap (PDF)
                </a>
              )}
            </div>

            {done ? (
              <div className="rounded-3xl border border-gold/30 bg-gradient-to-br from-night to-night-deep p-8 text-center shadow-soft md:p-12">
                <CheckCircle2 className="mx-auto h-12 w-12 text-gold" />
                <h2 className="mt-4 font-display text-2xl md:text-3xl">Bedankt voor je aanmelding!</h2>
                <p className="mt-3 text-foreground/75">
                  We hebben je aanvraag ontvangen. Een van onze partnermanagers neemt binnen enkele werkdagen
                  persoonlijk contact met je op om de mogelijkheden te bespreken.
                </p>
              </div>
            ) : (
              <form
                onSubmit={onSubmit}
                className="grid gap-6 rounded-3xl border border-gold/20 bg-night/60 p-6 shadow-soft md:p-10"
              >
                <div className="grid gap-5 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="company" className="text-xs uppercase tracking-widest text-foreground/75">Bedrijfsnaam *</Label>
                    <Input id="company" name="company" required maxLength={200} className="h-12 border-border bg-night-deep/60" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="contact_person" className="text-xs uppercase tracking-widest text-foreground/75">Contactpersoon *</Label>
                    <Input id="contact_person" name="contact_person" required maxLength={200} className="h-12 border-border bg-night-deep/60" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-xs uppercase tracking-widest text-foreground/75">E-mailadres *</Label>
                    <Input id="email" name="email" type="email" required maxLength={320} className="h-12 border-border bg-night-deep/60" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone" className="text-xs uppercase tracking-widest text-foreground/75">Telefoonnummer *</Label>
                    <Input id="phone" name="phone" type="tel" required maxLength={50} className="h-12 border-border bg-night-deep/60" />
                  </div>
                </div>

                <div className="space-y-3">
                  <p className="text-xs uppercase tracking-widest text-foreground/75">Gewenst partnerpakket *</p>
                  <RadioGroup
                    value={pkg}
                    onValueChange={(v) => setPkg(v as SponsorPackage)}
                    className="grid gap-2"
                  >
                    {SPONSOR_PACKAGES.map((p) => (
                      <label
                        key={p}
                        className={`flex cursor-pointer items-center gap-3 rounded-xl border p-4 transition-colors ${
                          pkg === p ? "border-gold/60 bg-night-deep/70" : "border-border bg-night-deep/40 hover:border-gold/40"
                        }`}
                      >
                        <RadioGroupItem value={p} />
                        <span className="text-sm">{SPONSOR_PACKAGE_LABELS[p]}</span>
                      </label>
                    ))}
                  </RadioGroup>
                </div>

                <div className="space-y-3">
                  <p className="text-xs uppercase tracking-widest text-foreground/75">Interesse in extra activatie</p>
                  <div className="grid gap-2 md:grid-cols-2">
                    {SPONSOR_ACTIVATIONS.map((a) => {
                      const checked = activations.includes(a);
                      return (
                        <label
                          key={a}
                          className={`flex cursor-pointer items-start gap-3 rounded-xl border p-4 transition-colors ${
                            checked ? "border-gold/60 bg-night-deep/70" : "border-border bg-night-deep/40 hover:border-gold/40"
                          }`}
                        >
                          <Checkbox checked={checked} onCheckedChange={() => toggleActivation(a)} className="mt-0.5" />
                          <span className="text-sm">{SPONSOR_ACTIVATION_LABELS[a]}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="logo" className="text-xs uppercase tracking-widest text-foreground/75">Logo uploaden *</Label>
                  <label
                    htmlFor="logo"
                    className="flex cursor-pointer items-center gap-3 rounded-xl border border-dashed border-gold/40 bg-night-deep/40 p-4 hover:border-gold/70"
                  >
                    <Upload className="h-5 w-5 text-gold" />
                    <span className="text-sm text-foreground/85">
                      {logoFile ? logoFile.name : "Kies bestand (PNG, JPG, WebP of SVG, max. 5MB)"}
                    </span>
                  </label>
                  <input
                    id="logo"
                    name="logo"
                    type="file"
                    accept="image/png,image/jpeg,image/webp,image/svg+xml"
                    className="hidden"
                    onChange={(e) => setLogoFile(e.currentTarget.files?.[0] ?? null)}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="notes" className="text-xs uppercase tracking-widest text-foreground/75">Opmerkingen</Label>
                  <Textarea id="notes" name="notes" maxLength={2000} rows={4} className="border-border bg-night-deep/60" />
                </div>

                <label className="flex items-start gap-3 rounded-xl border border-border bg-night-deep/40 p-4">
                  <Checkbox checked={terms} onCheckedChange={(v) => setTerms(v === true)} className="mt-0.5" />
                  <span className="text-sm text-foreground/85">
                    Ik ga akkoord met de partnervoorwaarden en geef toestemming om contact op te nemen over
                    mijn aanvraag. *
                  </span>
                </label>

                <Button type="submit" variant="hero" size="xl" disabled={submitting} className="w-full sm:w-auto">
                  {submitting ? "Versturen…" : "Verstuur aanvraag"}
                </Button>
              </form>
            )}
          </section>
        </main>
        <Footer />
        <Toaster position="top-center" />
      </div>
    </I18nProvider>
  );
}